import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { SimpleFormat } from 'shop/utils'
import { useLayoutEffect, useState } from 'react'
import useExpandableDescription from './useDescription'
import { BaseContainer, baseDescContainerStyles } from './commonStyles'
import { DescriptionProps, DescriptionType } from './types'
import SeeMoreControl from './SeeMoreControl'

const Description = ({
  description = '',
  forceShowDescription = false,
  type = DescriptionType.PRODUCT
}: DescriptionProps) => {
  const {
    ref,
    hasExpandedText,
    maxHeight,
    isTransitioning,
    toggleDescriptionExpansion
  } = useExpandableDescription({ forceShowDescription })
  const [isSeeMoreEnabled, setIsSeeMoreEnabled] = useState(false)

  // Detects whether we need to show the "Read more" or not
  useLayoutEffect(() => {
    // no need to set "Read more" if we are forcing to show the description
    if (ref.current && !forceShowDescription) {
      // Small 1px buffer to account for sub-pixel rendering differences
      const buffer = 1
      if (ref.current?.clientHeight + buffer < ref.current.scrollHeight) {
        setIsSeeMoreEnabled(true)
      }
    }
  }, [ref, forceShowDescription])

  const handleContainerClick = () => {
    if (isSeeMoreEnabled) {
      toggleDescriptionExpansion()
    }
  }

  if (!description) return <></>

  return (
    <Container
      isSeeMoreEnabled={isSeeMoreEnabled}
      onClick={handleContainerClick}
      type={type}
    >
      <DescriptionContainer
        data-testid='product-description'
        ref={ref}
        {...{
          hasExpanded: hasExpandedText,
          isSeeMoreEnabled,
          maxHeight: maxHeight,
          isTransitioning,
          type
        }}
      >
        <SimpleFormat>{description}</SimpleFormat>
      </DescriptionContainer>
      {isSeeMoreEnabled && (
        <SeeMoreControl
          hasExpandedText={hasExpandedText}
          handleClick={toggleDescriptionExpansion}
        >
          {hasExpandedText ? 'Read less' : 'Read more'}
        </SeeMoreControl>
      )}
    </Container>
  )
}

const Container = styled(BaseContainer)<
  StyledHTMLElement & { isSeeMoreEnabled: boolean; type: DescriptionType }
>(({ isSeeMoreEnabled, type }) => ({
  cursor: isSeeMoreEnabled ? 'pointer' : 'default',
  marginBottom: type === DescriptionType.PRODUCT ? '24px' : '0px'
}))

const DescriptionContainer = styled(baseDescContainerStyles)<
  StyledHTMLElement & {
    hasExpanded: boolean
    isSeeMoreEnabled: boolean
    isTransitioning: boolean
    type: DescriptionType
    maxHeight?: number
  }
>(({ hasExpanded, maxHeight, isTransitioning, type }) => {
  const collapsedMaxHeight = type === DescriptionType.PRODUCT ? 46 : 34
  return {
    lineHeight: type === DescriptionType.PRODUCT ? '1.1' : '1.2',

    ...(!hasExpanded &&
      !isTransitioning && {
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: type === DescriptionType.PRODUCT ? 3 : 2
      }),

    maxHeight: hasExpanded
      ? `${maxHeight || 2000}px`
      : `${collapsedMaxHeight}px`
  }
})

export default Description
